import { useState } from 'react'
import { IoChevronDown, IoAdd, IoCheckmark, IoTrendingUp, IoTrendingDown, IoRemove } from 'react-icons/io5'
import type { PlayerFoulsCardsItem } from '../../types'
import { COUNTRY_FLAGS } from '../../context/LeagueContext'

interface PlayerFoulsCardsTableProps {
  items: PlayerFoulsCardsItem[]
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onSortChange: (column: string) => void
}

const COLUMNS = [
  { key: 'league', label: 'League', sortable: false, width: '55px', tooltip: 'Competition the match is in' },
  { key: 'fixture', label: 'Fixture', sortable: false, width: '150px', tooltip: 'Home team vs Away team' },
  { key: 'kickoff', label: 'Kickoff', sortable: true, sortKey: 'kickoff', width: '80px', tooltip: 'Match kickoff time in your local timezone' },
  { key: 'player', label: 'Player', sortable: false, width: '120px', tooltip: 'Player name' },
  { key: 'position', label: 'Pos', sortable: false, width: '45px', tooltip: 'Player position: FWD (Forward), MID (Midfielder), DEF (Defender), GK (Goalkeeper)' },
  { key: 'fouls_committed', label: 'Fouls', sortable: true, sortKey: 'fouls_committed', width: '65px', tooltip: 'Average fouls committed per game over last 5 matches. Arrow shows trend: ↑ = increasing (last 3 > last 5), ↓ = decreasing' },
  { key: 'fouls_drawn', label: 'Fouled', sortable: true, sortKey: 'fouls_drawn', width: '70px', tooltip: 'Average times fouled per game over last 5 matches. Arrow shows trend: ↑ = increasing (last 3 > last 5), ↓ = decreasing' },
  { key: 'tackles', label: 'Tckls', sortable: true, sortKey: 'tackles', width: '50px', tooltip: 'Average tackles per game over last 5 matches' },
  { key: 'cards', label: 'Card %', sortable: true, sortKey: 'cards', width: '65px', tooltip: 'Model confidence player receives a card. Green (60%+) = High, Yellow (40-59%) = Medium, Red (<40%) = Low' },
  { key: 'ref_foul', label: 'Ref F', sortable: false, width: '55px', tooltip: 'Referee foul factor - how often this ref calls fouls vs average. >1 = calls more fouls, <1 = calls fewer fouls' },
  { key: 'ref_card', label: 'Ref C', sortable: true, sortKey: 'ref_factor', width: '55px', tooltip: 'Referee card factor - how strict with cards. Red (1.2x+) = strict, Yellow (1.05-1.19x) = average, Green (<1.05x) = lenient' },
  { key: 'action', label: '', sortable: false, width: '75px', tooltip: '' },
]

function formatKickoff(isoDate: string): string {
  const date = new Date(isoDate)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const day = days[date.getDay()]
  const hours = date.getHours().toString().padStart(2, '0')
  const mins = date.getMinutes().toString().padStart(2, '0')
  return `${day} ${hours}:${mins}`
}

function getPositionClass(position: string | null): string {
  if (!position) return ''
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'attacker'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'midfielder'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'defender'
  return ''
}

function getPositionLabel(position: string | null): string {
  if (!position) return '-'
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'FWD'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'MID'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'DEF'
  if (['goalkeeper', 'g', 'gk'].includes(pos)) return 'GK'
  return pos.substring(0, 3).toUpperCase()
}

function getProbabilityClass(prob: number): string {
  if (prob >= 60) return 'prob-high'
  if (prob >= 40) return 'prob-med'
  return 'prob-low'
}

function getRefFactorClass(factor: number): string {
  if (factor >= 1.2) return 'ref-high'   // Red - strict ref
  if (factor >= 1.05) return 'ref-med'   // Yellow - average
  return 'ref-low'                        // Green - lenient
}

function getCountryFlag(country: string | null): string {
  if (!country) return ''
  return COUNTRY_FLAGS[country] || ''
}

function getTrendIcon(trend: 'up' | 'down' | 'flat' | null, statType: 'fouls' | 'fouled') {
  if (!trend) return null
  const label = statType === 'fouls' ? 'fouls committed' : 'times fouled'
  if (trend === 'up') {
    return (
      <IoTrendingUp
        className="trend-icon trend-up"
        size={12}
        title={`Trending up: ${label} increasing (last 3 games > last 5 avg)`}
      />
    )
  }
  if (trend === 'down') {
    return (
      <IoTrendingDown
        className="trend-icon trend-down"
        size={12}
        title={`Trending down: ${label} decreasing (last 3 games < last 5 avg)`}
      />
    )
  }
  return (
    <IoRemove
      className="trend-icon trend-flat"
      size={12}
      title={`Stable: ${label} consistent (last 3 ≈ last 5 avg)`}
    />
  )
}

export function PlayerFoulsCardsTable({ items, sortBy, sortOrder, onSortChange }: PlayerFoulsCardsTableProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [addedPlayers, setAddedPlayers] = useState<Set<string>>(new Set())
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null)

  const handleRowClick = (playerId: number) => {
    setExpandedId(expandedId === playerId ? null : playerId)
    setDropdownOpen(null)
  }

  const handleAddClick = (e: React.MouseEvent, playerId: number) => {
    e.stopPropagation()
    setDropdownOpen(dropdownOpen === playerId ? null : playerId)
  }

  const handleAddOption = (e: React.MouseEvent, item: PlayerFoulsCardsItem, marketType: string) => {
    e.stopPropagation()
    // TODO: Integrate with bet builder context
    const key = `${item.player_id}-${marketType}`
    setAddedPlayers((prev) => new Set(prev).add(key))
    setDropdownOpen(null)
  }

  const renderSortIcon = (column: typeof COLUMNS[0]) => {
    if (!column.sortable || !column.sortKey) return null
    if (sortBy !== column.sortKey) {
      return <span className="sort-icon sort-icon-inactive">{'\u2195'}</span>
    }
    return (
      <span className="sort-icon">
        {sortOrder === 'desc' ? '\u2193' : '\u2191'}
      </span>
    )
  }

  return (
    <div className="player-stats-table-container">
      <table className="player-stats-table fouls-cards-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                className={col.sortable ? 'sortable' : ''}
                onClick={() => col.sortable && col.sortKey && onSortChange(col.sortKey)}
                title={col.tooltip}
              >
                {col.label}
                {renderSortIcon(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <>
              <tr
                key={`row-${item.fixture_id}-${item.player_id}`}
                onClick={() => handleRowClick(item.player_id)}
                className={expandedId === item.player_id ? 'expanded' : ''}
              >
                <td>
                  <span title={item.league_name}>
                    {getCountryFlag(item.league_country)}
                  </span>
                </td>
                <td className="fixture-cell">
                  <span className="fixture-label" title={item.fixture_label}>
                    {item.fixture_label}
                  </span>
                </td>
                <td>{formatKickoff(item.kickoff_utc)}</td>
                <td className="player-cell">
                  <span className="player-name">{item.player_name}</span>
                </td>
                <td>
                  <span className={`position-badge ${getPositionClass(item.position)}`}>
                    {getPositionLabel(item.position)}
                  </span>
                </td>
                <td className="stat-cell">
                  <span className="stat-with-trend">
                    {item.fouls_committed_avg.toFixed(1)}
                    {getTrendIcon(item.fouls_committed_trend, 'fouls')}
                  </span>
                </td>
                <td className="stat-cell">
                  <span className="stat-with-trend">
                    {item.fouls_drawn_avg.toFixed(1)}
                    {getTrendIcon(item.fouls_drawn_trend, 'fouled')}
                  </span>
                </td>
                <td className="stat-cell">{item.tackles_avg.toFixed(1)}</td>
                <td>
                  <span className={`prob-badge ${getProbabilityClass(item.cards_conviction_pct)}`}>
                    {item.cards_conviction_pct}%
                  </span>
                </td>
                <td>
                  <span className={`ref-badge ${getRefFactorClass(item.ref_foul_factor)}`}>
                    {item.ref_foul_factor.toFixed(2)}x
                  </span>
                </td>
                <td>
                  <span className={`ref-badge ${getRefFactorClass(item.ref_card_factor)}`}>
                    {item.ref_card_factor.toFixed(2)}x
                  </span>
                </td>
                <td className="action-cell">
                  <div className="player-add-dropdown">
                    <button
                      className="player-stats-add-btn"
                      onClick={(e) => handleAddClick(e, item.player_id)}
                    >
                      <IoAdd size={14} /> Add <IoChevronDown size={12} />
                    </button>
                    {dropdownOpen === item.player_id && (
                      <div className="dropdown-menu">
                        <button
                          onClick={(e) => handleAddOption(e, item, 'fouls')}
                          disabled={addedPlayers.has(`${item.player_id}-fouls`)}
                        >
                          {addedPlayers.has(`${item.player_id}-fouls`) ? <IoCheckmark size={12} /> : null}
                          Fouls O/U
                        </button>
                        <button
                          onClick={(e) => handleAddOption(e, item, 'fouled')}
                          disabled={addedPlayers.has(`${item.player_id}-fouled`)}
                        >
                          {addedPlayers.has(`${item.player_id}-fouled`) ? <IoCheckmark size={12} /> : null}
                          To Be Fouled
                        </button>
                        <button
                          onClick={(e) => handleAddOption(e, item, 'card')}
                          disabled={addedPlayers.has(`${item.player_id}-card`)}
                        >
                          {addedPlayers.has(`${item.player_id}-card`) ? <IoCheckmark size={12} /> : null}
                          To Be Carded
                        </button>
                        {item.tackles_market_id && (
                          <button
                            onClick={(e) => handleAddOption(e, item, 'tackles')}
                            disabled={addedPlayers.has(`${item.player_id}-tackles`)}
                          >
                            {addedPlayers.has(`${item.player_id}-tackles`) ? <IoCheckmark size={12} /> : null}
                            Tackles O/U
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
              {expandedId === item.player_id && (
                <tr key={`expanded-${item.fixture_id}-${item.player_id}`} className="player-stats-expanded-row">
                  <td colSpan={COLUMNS.length}>
                    <div className="player-stats-expanded-content">
                      <div className="player-stats-expanded-stat">
                        <span className="label">Season Fouls Avg</span>
                        <span className="value">{item.season_fouls_avg != null ? item.season_fouls_avg.toFixed(2) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Season Cards Avg</span>
                        <span className="value">{item.season_cards_avg != null ? item.season_cards_avg.toFixed(2) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Exp Minutes</span>
                        <span className="value">{item.expected_minutes != null ? Math.round(item.expected_minutes) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Role</span>
                        <span className="value">{item.role || '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Ref Foul Factor</span>
                        <span className="value">{item.ref_foul_factor.toFixed(2)}x</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Position Factor</span>
                        <span className="value">{item.position_factor.toFixed(2)}x</span>
                      </div>
                      <div className="player-stats-expanded-why">
                        <strong>Fouls Analysis:</strong> {item.why_fouls || 'No analysis available'}
                      </div>
                      <div className="player-stats-expanded-why">
                        <strong>Cards Analysis:</strong> {item.why_cards || 'No analysis available'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  )
}
